import { useEffect, useRef } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

/**
 * Scroll handling on every route change.
 *
 * Mounts once inside <BrowserRouter> above <Routes>. Behavior depends on
 * the navigation action that triggered the location change AND the
 * `location.state.restoreScroll` flag (opt-in per-link signal):
 *
 *   PUSH / REPLACE  → scroll to (0, 0) so a new page always starts at the
 *                     top. UNLESS:
 *                       (a) the URL contains a hash fragment — yield to the
 *                           browser's native anchor scroll so SPEAKERS /
 *                           EXPERIENCES / FAQ nav links continue to smooth-
 *                           scroll to their target section; OR
 *                       (b) the Link was rendered with
 *                           `state={{ restoreScroll: true }}` — run the
 *                           same restore block as POP, so the sticky
 *                           "Back to Home" link on /speakers and /gallery
 *                           can land the user back at their saved homepage
 *                           position (the value written synchronously by
 *                           SpeakersSection / PartnersGallerySection
 *                           before they called `navigate(...)`). If no
 *                           saved Y exists, this branch falls back to
 *                           scrollTo(0,0) — worst case = top of page, no
 *                           regression versus current behavior.
 *
 *   POP             → restore the previous scroll position for the target
 *                     pathname if one was synchronously saved by a forward
 *                     navigation handler. Image reflow on long pages is
 *                     handled by a ResizeObserver that retries the scroll
 *                     as the document grows, with a 2-second hard safety
 *                     cap. If no saved position exists, we DO NOTHING —
 *                     letting the browser's default scroll restoration
 *                     take over.
 *
 * Renders nothing.
 */

const KEY_PREFIX = 'scrollPos:';
const SAFETY_MS = 2000;
const INITIAL_PAINT_MS = 50;

export default function ScrollToTop() {
  const { pathname, hash, state } = useLocation();
  const navType = useNavigationType(); // 'POP' | 'PUSH' | 'REPLACE'
  // Opt-in per-link signal: a <Link to="/" state={{ restoreScroll: true }}>
  // asks us to treat this PUSH/REPLACE like a POP, restoring the saved
  // scrollPos:<pathname> for the destination instead of scrolling to top.
  const wantsRestore = !!(state && state.restoreScroll);
  // Track whether a restore is in progress so any concurrent effects can
  // tell. Currently only used by this component; exposed via ref to make
  // future coordination trivial.
  const isRestoringRef = useRef(false);

  useEffect(() => {
    // Hash fragments: always defer to browser-native anchor scroll, on every
    // navigation action. Preserves the prior behavior exactly.
    if (hash) return undefined;

    if (navType === 'POP' || wantsRestore) {
      // Read the saved Y for this pathname. Synchronously written by the
      // forward-navigation handlers (SpeakersSection / PartnersGallerySection)
      // just before they called `navigate()`, so it's guaranteed-current.
      let saved = 0;
      try {
        const raw = sessionStorage.getItem(KEY_PREFIX + pathname);
        if (raw) saved = parseInt(raw, 10) || 0;
      } catch (_) {
        /* sessionStorage may be unavailable (private mode, quota, etc.) */
      }

      // No saved position branches:
      //   • POP → do nothing. Browser's default scrollRestoration ('auto')
      //     will handle it. We intentionally do NOT call scrollTo(0,0) on
      //     POP, because that would clobber the browser's own restoration
      //     attempt and re-introduce the original bug.
      //   • wantsRestore PUSH/REPLACE → fall back to scrollTo(0,0) so the
      //     user doesn't get stranded mid-page on a forward navigation.
      if (saved <= 0) {
        if (wantsRestore) {
          try {
            window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
          } catch (_) {
            window.scrollTo(0, 0);
          }
        }
        return undefined;
      }

      // Begin a restoration sequence.
      isRestoringRef.current = true;
      let done = false;
      let initialTimer = null;
      let resizeObs = null;
      let safetyTimer = null;

      const finish = () => {
        if (done) return;
        done = true;
        isRestoringRef.current = false;
        if (initialTimer) { clearTimeout(initialTimer); initialTimer = null; }
        if (safetyTimer) { clearTimeout(safetyTimer); safetyTimer = null; }
        if (resizeObs) { resizeObs.disconnect(); resizeObs = null; }
      };

      const tryScroll = () => {
        if (done) return;
        const docHeight = document.documentElement.scrollHeight
          || document.body.scrollHeight
          || 0;
        const maxScrollable = docHeight - window.innerHeight;
        // Only attempt the scroll once the document is tall enough to land
        // the target. Image reflow on the homepage can grow scrollHeight
        // after first paint; the ResizeObserver below re-fires us as that
        // happens.
        if (maxScrollable >= saved - 4) {
          try {
            window.scrollTo({ top: saved, left: 0, behavior: 'instant' });
          } catch (_) {
            window.scrollTo(0, saved);
          }
          // Confirm the scroll landed before declaring success.
          if (window.scrollY >= saved - 4) {
            finish();
          }
        }
      };

      // Initial attempt after a short paint window, then install the
      // ResizeObserver so subsequent layout growth (lazy images, async
      // content) lets us land precisely.
      initialTimer = setTimeout(() => {
        if (done) return;
        tryScroll();
        if (done) return;
        if (typeof ResizeObserver !== 'undefined') {
          resizeObs = new ResizeObserver(() => tryScroll());
          // Observe both <html> and <body>: different browsers report
          // height changes on one or the other depending on layout mode.
          try { resizeObs.observe(document.documentElement); } catch (_) { /* ignore */ }
          try { resizeObs.observe(document.body); } catch (_) { /* ignore */ }
        }
        // Hard 2-second cap. Much shorter than the prior v3.4 attempt
        // (which used 30s) — accepts a worst-case "stay at top" outcome
        // rather than risk a long-lived observer race.
        safetyTimer = setTimeout(finish, SAFETY_MS);
      }, INITIAL_PAINT_MS);

      return finish;
    }

    // PUSH / REPLACE — forward navigation. Always start at the top.
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
    } catch (_) {
      // Fallback for browsers that don't accept 'instant' as ScrollBehavior.
      window.scrollTo(0, 0);
    }
    return undefined;
  }, [pathname, hash, navType, wantsRestore]);

  return null;
}
